import React, { useState } from 'react';
import BrochureGate from '../components/BrochureGate';
import { downloadServerPDF } from '../lib/pdf-download';
import { LANGUAGES, tr, TRANSLATIONS } from '../data/salesPrimerCopy';

/**
 * Sales Primer, 8-page A4 internal sales onboarding brochure.
 *
 * Built for sales people who are NOT technical. Heavy use of icons,
 * coloured cards, big type. Minimal prose. Multilingual: en / ar / uk / ru.
 *
 * Gated behind the same access gate as /brochure. Linked from the
 * brochure toolbar.
 */

const COLORS = {
  navy: '#0A2540',
  navyDeep: '#061A2E',
  navyLight: '#163A5F',
  slate: '#2A3B5A',
  muted: '#5B6B82',
  border: '#E5DFD3',
  bgSoft: '#F7F4EE',
  cream: '#FAF8F5',
  magenta: '#D6336C',
  magentaDeep: '#A22456',
  gold: '#C99A2E',
  emerald: '#1F7A4C',
  paper: '#FAFAF7',
};

// ─── Layout primitives ─────────────────────────────────────────────
const Page = ({ children, bg = '#fff', last = false, dir = 'ltr' }) => (
  <section
    className="primer-page"
    dir={dir}
    style={{
      width: '210mm',
      height: '297mm',
      boxSizing: 'border-box',
      background: bg,
      position: 'relative',
      overflow: 'hidden',
      pageBreakAfter: last ? 'auto' : 'always',
      breakAfter: last ? 'auto' : 'page',
      boxShadow: '0 8px 32px rgba(10, 37, 64, 0.12)',
      margin: '0 auto 24px',
      padding: '14mm 14mm',
      fontFamily:
        dir === 'rtl'
          ? "'Cairo', 'Tajawal', 'Inter', sans-serif"
          : "'Inter', 'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif",
      color: COLORS.navy,
    }}
  >
    {children}
  </section>
);

const PageHeader = ({ pageNum, lang }) => (
  <div style={{
    position: 'absolute', top: '8mm', left: '14mm', right: '14mm',
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    fontSize: '8pt', color: COLORS.muted,
    letterSpacing: '0.08em', textTransform: 'uppercase', fontWeight: 600,
  }}>
    <span><span style={{ color: COLORS.magenta }}>●</span>  Keystone · Sales primer · {lang.toUpperCase()} · Internal</span>
    <span style={{ color: COLORS.navy, fontWeight: 700 }}>{pageNum} / 10</span>
  </div>
);

const PageFooter = () => (
  <div style={{
    position: 'absolute', bottom: '8mm', left: '14mm', right: '14mm',
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    fontSize: '7.5pt', color: COLORS.muted,
    borderTop: `1px solid ${COLORS.border}`, paddingTop: '4mm',
  }}>
    <span>Keystone by CloudVoro · Sales onboarding · Do not share externally</span>
    <span style={{ color: COLORS.magenta, fontWeight: 600 }}>Confidential</span>
  </div>
);

const SectionLabel = ({ children, color = COLORS.magenta }) => (
  <div style={{ fontSize: '7.5pt', letterSpacing: '0.18em', textTransform: 'uppercase', fontWeight: 700, color, marginBottom: '3mm' }}>
    {children}
  </div>
);

const PageTitle = ({ children }) => (
  <h2 style={{
    fontFamily: "'Playfair Display', Georgia, serif",
    fontSize: '24pt', fontWeight: 700,
    color: COLORS.navy, lineHeight: 1.1,
    margin: '0 0 6mm 0', letterSpacing: '-0.02em',
  }}>
    {children}
  </h2>
);

const Kicker = ({ children, color = COLORS.magenta }) => (
  <div style={{
    marginTop: '6mm', padding: '3.5mm 5mm',
    background: COLORS.bgSoft,
    borderLeft: `3px solid ${color}`,
    fontSize: '9.5pt', color: COLORS.navy,
    fontStyle: 'italic', lineHeight: 1.45, fontWeight: 500,
  }}>
    {children}
  </div>
);

// ─── Pages ─────────────────────────────────────────────────────────
const PageCover = ({ lang, dir }) => (
  <Page bg={COLORS.cream} dir={dir}>
    <div style={{ position: 'absolute', top: '14mm', left: '14mm', right: '14mm' }}>
      <div style={{ fontSize: '11pt', letterSpacing: '0.24em', color: COLORS.magenta, textTransform: 'uppercase', fontWeight: 800 }}>
        Keystone
      </div>
      <div style={{ fontSize: '8pt', color: COLORS.muted, marginTop: 2, letterSpacing: '0.1em', textTransform: 'uppercase', fontWeight: 600 }}>
        by CloudVoro
      </div>
    </div>

    <div style={{ marginTop: '50mm' }}>
      <div style={{ fontSize: '8.5pt', letterSpacing: '0.2em', textTransform: 'uppercase', fontWeight: 700, color: COLORS.gold, marginBottom: '6mm' }}>
        {tr('cover_eyebrow', lang)}
      </div>
      <h1 style={{
        fontFamily: "'Playfair Display', Georgia, serif",
        fontSize: '42pt', fontWeight: 700, color: COLORS.navy,
        lineHeight: 1.0, letterSpacing: '-0.025em',
        margin: '0 0 8mm 0', maxWidth: '170mm',
      }}>
        {tr('cover_title', lang)}
      </h1>
      <p style={{ fontSize: '12pt', color: COLORS.slate, lineHeight: 1.5, maxWidth: '170mm', margin: 0 }}>
        {tr('cover_lede', lang)}
      </p>
    </div>

    {/* Nav chips */}
    <div style={{
      position: 'absolute', bottom: '32mm', left: '14mm', right: '14mm',
      display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '4mm',
    }}>
      {[
        ['02', 'cover_chip_what', COLORS.magenta],
        ['04', 'cover_chip_who', COLORS.emerald],
        ['07', 'cover_chip_how', COLORS.gold],
        ['09', 'cover_chip_hand', COLORS.navyLight],
      ].map(([num, key, c]) => (
        <div key={num} style={{
          padding: '4mm', background: '#fff',
          border: `1px solid ${COLORS.border}`, borderTop: `3px solid ${c}`,
          borderRadius: 4,
        }}>
          <div style={{ fontSize: '7pt', color: c, fontWeight: 800, letterSpacing: '0.16em', marginBottom: '1mm' }}>
            P{num}
          </div>
          <div style={{ fontSize: '9pt', color: COLORS.navy, fontWeight: 700, lineHeight: 1.25 }}>
            {tr(key, lang)}
          </div>
        </div>
      ))}
    </div>

    <div style={{
      position: 'absolute', bottom: '14mm', left: '14mm', right: '14mm',
      paddingTop: '4mm', borderTop: `1px solid ${COLORS.border}`,
      display: 'flex', justifyContent: 'space-between',
      fontSize: '7.5pt', color: COLORS.muted, fontWeight: 600,
    }}>
      <span style={{ letterSpacing: '0.08em', textTransform: 'uppercase' }}>Internal · Sales handbook · v1</span>
      <span style={{ letterSpacing: '0.08em', textTransform: 'uppercase' }}>01 / 10</span>
    </div>
  </Page>
);

const PageWhatIs = ({ lang, dir }) => (
  <Page dir={dir}>
    <PageHeader pageNum="02" lang={lang} />
    <div style={{ marginTop: '14mm' }}>
      <SectionLabel>{tr('p2_section', lang)}</SectionLabel>
      <PageTitle>{tr('p2_title', lang)}</PageTitle>
    </div>

    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '5mm', marginTop: '8mm' }}>
      {[
        ['p2_one_a', 'p2_one_b', COLORS.gold, '1'],
        ['p2_two_a', 'p2_two_b', COLORS.slate, '2'],
        ['p2_three_a', 'p2_three_b', COLORS.magenta, '3'],
      ].map(([a, b, c, n]) => (
        <div key={n} style={{
          padding: '6mm 5mm', background: '#fff',
          border: `1px solid ${COLORS.border}`, borderTop: `4px solid ${c}`,
          borderRadius: 4, minHeight: '95mm',
        }}>
          <div style={{
            fontFamily: "'Playfair Display', Georgia, serif", fontSize: '40pt',
            fontWeight: 700, color: c, lineHeight: 1, letterSpacing: '-0.02em',
            marginBottom: '4mm',
          }}>{n}</div>
          <div style={{ fontSize: '13pt', fontWeight: 700, color: COLORS.navy, lineHeight: 1.2, marginBottom: '3mm' }}>
            {tr(a, lang)}
          </div>
          <div style={{ fontSize: '10pt', color: COLORS.slate, lineHeight: 1.5 }}>
            {tr(b, lang)}
          </div>
        </div>
      ))}
    </div>

    <Kicker color={COLORS.magenta}>{tr('p2_kicker', lang)}</Kicker>
    <PageFooter />
  </Page>
);

const PageICP = ({ lang, dir }) => (
  <Page dir={dir}>
    <PageHeader pageNum="04" lang={lang} />
    <div style={{ marginTop: '14mm' }}>
      <SectionLabel color={COLORS.emerald}>{tr('p3_section', lang)}</SectionLabel>
      <PageTitle>{tr('p3_title', lang)}</PageTitle>
    </div>

    {/* Buyer profile card */}
    <div style={{
      background: '#fff', border: `1px solid ${COLORS.border}`,
      borderLeft: `4px solid ${COLORS.emerald}`, borderRadius: 4,
      padding: '6mm 7mm', marginTop: '5mm',
    }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '5mm', rowGap: '5mm' }}>
        {[
          ['p3_role_label', 'p3_role'],
          ['p3_age_label', 'p3_age'],
          ['p3_loc_label', 'p3_loc'],
          ['p3_team_label', 'p3_team'],
          ['p3_pain_label', 'p3_pain'],
          ['p3_want_label', 'p3_want'],
        ].map(([lbl, val]) => (
          <div key={lbl}>
            <div style={{ fontSize: '7pt', color: COLORS.muted, textTransform: 'uppercase', letterSpacing: '0.14em', fontWeight: 700, marginBottom: '1.5mm' }}>
              {tr(lbl, lang)}
            </div>
            <div style={{ fontSize: '10pt', color: COLORS.navy, fontWeight: 600, lineHeight: 1.35 }}>
              {tr(val, lang)}
            </div>
          </div>
        ))}
      </div>
    </div>

    {/* Personas */}
    <div style={{ marginTop: '7mm' }}>
      <SectionLabel color={COLORS.gold}>{tr('p3_personas_label', lang)}</SectionLabel>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '4mm' }}>
        {[
          ['🧀', 'p3_persona_cheese', COLORS.gold],
          ['🥖', 'p3_persona_bakery', COLORS.magenta],
          ['🥓', 'p3_persona_butcher', COLORS.magentaDeep],
          ['🍯', 'p3_persona_sauce', COLORS.emerald],
        ].map(([icon, key, c]) => (
          <div key={key} style={{
            padding: '5mm 4mm', textAlign: 'center',
            background: COLORS.cream, border: `1px solid ${COLORS.border}`,
            borderRadius: 4,
          }}>
            <div style={{ fontSize: '24pt', marginBottom: '2mm' }}>{icon}</div>
            <div style={{ fontSize: '9pt', color: c, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              {tr(key, lang)}
            </div>
          </div>
        ))}
      </div>
    </div>

    <Kicker color={COLORS.emerald}>{tr('p3_kicker', lang)}</Kicker>
    <PageFooter />
  </Page>
);

const PageWhatItDoes = ({ lang, dir }) => (
  <Page dir={dir}>
    <PageHeader pageNum="05" lang={lang} />
    <div style={{ marginTop: '14mm' }}>
      <SectionLabel>{tr('p4_section', lang)}</SectionLabel>
      <PageTitle>{tr('p4_title', lang)}</PageTitle>
    </div>

    {/* Chain visual */}
    <div style={{ marginTop: '14mm' }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '2mm', alignItems: 'stretch' }}>
        {[
          ['🥛', 'p4_step1', 'p4_step1_sub'],
          ['🗄️', 'p4_step2', 'p4_step2_sub'],
          ['🧑‍🍳', 'p4_step3', 'p4_step3_sub'],
          ['📦', 'p4_step4', 'p4_step4_sub'],
          ['🚚', 'p4_step5', 'p4_step5_sub'],
        ].map(([icon, title, sub], i, arr) => (
          <React.Fragment key={i}>
            <div style={{
              padding: '6mm 3mm', textAlign: 'center', background: '#fff',
              border: `1px solid ${COLORS.border}`, borderRadius: 4,
              borderTop: `3px solid ${i === 4 ? COLORS.magenta : COLORS.navy}`,
              minHeight: '40mm', display: 'flex', flexDirection: 'column',
              justifyContent: 'center', position: 'relative',
            }}>
              <div style={{ fontSize: '24pt', marginBottom: '2mm' }}>{icon}</div>
              <div style={{ fontSize: '10pt', fontWeight: 800, color: COLORS.navy, marginBottom: '1mm', lineHeight: 1.2 }}>
                {tr(title, lang)}
              </div>
              <div style={{ fontSize: '8pt', color: COLORS.muted, lineHeight: 1.35 }}>
                {tr(sub, lang)}
              </div>
              {i < arr.length - 1 && (
                <div style={{
                  position: 'absolute',
                  right: dir === 'rtl' ? 'auto' : '-3mm', left: dir === 'rtl' ? '-3mm' : 'auto',
                  top: '50%', transform: 'translateY(-50%)',
                  width: '6mm', height: '6mm',
                  background: COLORS.magenta, color: '#fff',
                  borderRadius: '50%', display: 'flex',
                  alignItems: 'center', justifyContent: 'center',
                  fontSize: '11pt', fontWeight: 800, zIndex: 2,
                }}>
                  {dir === 'rtl' ? '←' : '→'}
                </div>
              )}
            </div>
          </React.Fragment>
        ))}
      </div>
    </div>

    {/* Kicker stat */}
    <div style={{
      marginTop: '14mm', padding: '6mm 7mm',
      background: COLORS.navy, color: '#fff', borderRadius: 6,
      display: 'grid', gridTemplateColumns: '1fr 2.4fr', gap: '7mm', alignItems: 'center',
    }}>
      <div>
        <div style={{ fontSize: '8pt', color: COLORS.gold, textTransform: 'uppercase', letterSpacing: '0.16em', fontWeight: 700, marginBottom: '2mm' }}>
          90s
        </div>
        <div style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: '32pt', fontWeight: 700, lineHeight: 1, color: '#fff' }}>
          {tr('p4_kicker_label', lang)}
        </div>
      </div>
      <div style={{ fontSize: '11pt', color: '#E5EAF0', lineHeight: 1.5 }}>
        {tr('p4_kicker_body', lang)}
      </div>
    </div>

    <PageFooter />
  </Page>
);

const PageHowToTalk = ({ lang, dir }) => {
  const sayList = TRANSLATIONS.p5_say[lang] || TRANSLATIONS.p5_say.en;
  const dontList = TRANSLATIONS.p5_dont[lang] || TRANSLATIONS.p5_dont.en;
  return (
    <Page dir={dir}>
      <PageHeader pageNum="07" lang={lang} />
      <div style={{ marginTop: '14mm' }}>
        <SectionLabel color={COLORS.gold}>{tr('p5_section', lang)}</SectionLabel>
        <PageTitle>{tr('p5_title', lang)}</PageTitle>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6mm', marginTop: '5mm' }}>
        {/* SAY */}
        <div style={{ padding: '5mm 6mm', background: '#fff', border: `1px solid ${COLORS.emerald}`, borderRadius: 4 }}>
          <div style={{
            display: 'inline-block', padding: '1mm 3mm', marginBottom: '4mm',
            background: COLORS.emerald, color: '#fff', fontSize: '8pt',
            fontWeight: 800, letterSpacing: '0.16em', textTransform: 'uppercase', borderRadius: 3,
          }}>
            ✓ {tr('p5_say_label', lang)}
          </div>
          <ul style={{ margin: 0, paddingLeft: dir === 'rtl' ? 0 : 16, paddingRight: dir === 'rtl' ? 16 : 0, listStyle: 'none' }}>
            {sayList.map((s, i) => (
              <li key={i} style={{ fontSize: '10pt', color: COLORS.navy, lineHeight: 1.5, marginBottom: '3mm', paddingLeft: dir === 'rtl' ? 0 : 8, paddingRight: dir === 'rtl' ? 8 : 0, borderLeft: dir === 'rtl' ? 'none' : `2px solid ${COLORS.emerald}`, borderRight: dir === 'rtl' ? `2px solid ${COLORS.emerald}` : 'none' }}>
                {s}
              </li>
            ))}
          </ul>
        </div>

        {/* DON'T */}
        <div style={{ padding: '5mm 6mm', background: '#fff', border: `1px solid ${COLORS.magenta}`, borderRadius: 4 }}>
          <div style={{
            display: 'inline-block', padding: '1mm 3mm', marginBottom: '4mm',
            background: COLORS.magenta, color: '#fff', fontSize: '8pt',
            fontWeight: 800, letterSpacing: '0.16em', textTransform: 'uppercase', borderRadius: 3,
          }}>
            ✗ {tr('p5_dont_label', lang)}
          </div>
          <ul style={{ margin: 0, paddingLeft: dir === 'rtl' ? 0 : 16, paddingRight: dir === 'rtl' ? 16 : 0, listStyle: 'none' }}>
            {dontList.map((s, i) => (
              <li key={i} style={{ fontSize: '10pt', color: COLORS.muted, fontStyle: 'italic', lineHeight: 1.5, marginBottom: '3mm', paddingLeft: dir === 'rtl' ? 0 : 8, paddingRight: dir === 'rtl' ? 8 : 0, borderLeft: dir === 'rtl' ? 'none' : `2px solid ${COLORS.magenta}`, borderRight: dir === 'rtl' ? `2px solid ${COLORS.magenta}` : 'none', textDecoration: 'line-through', textDecorationColor: 'rgba(214,51,108,0.4)' }}>
                {s}
              </li>
            ))}
          </ul>
        </div>
      </div>

      <Kicker color={COLORS.gold}>{tr('p5_kicker', lang)}</Kicker>
      <PageFooter />
    </Page>
  );
};

const PageQuestions = ({ lang, dir }) => (
  <Page dir={dir}>
    <PageHeader pageNum="08" lang={lang} />
    <div style={{ marginTop: '14mm' }}>
      <SectionLabel>{tr('p6_section', lang)}</SectionLabel>
      <PageTitle>{tr('p6_title', lang)}</PageTitle>
    </div>

    <div style={{ display: 'flex', flexDirection: 'column', gap: '3mm', marginTop: '5mm' }}>
      {[
        ['p6_q1', 'p6_a1', COLORS.magenta],
        ['p6_q2', 'p6_a2', COLORS.emerald],
        ['p6_q3', 'p6_a3', COLORS.gold],
        ['p6_q4', 'p6_a4', COLORS.navyLight],
        ['p6_q5', 'p6_a5', COLORS.magentaDeep],
      ].map(([q, a, c], i) => (
        <div key={q} style={{
          display: 'grid', gridTemplateColumns: '12mm 1fr',
          gap: '4mm', alignItems: 'start',
          padding: '4mm 5mm', background: '#fff',
          border: `1px solid ${COLORS.border}`, borderRadius: 4,
          borderLeft: dir === 'rtl' ? 'none' : `3px solid ${c}`,
          borderRight: dir === 'rtl' ? `3px solid ${c}` : 'none',
        }}>
          <div style={{
            fontFamily: "'Playfair Display', Georgia, serif",
            fontSize: '22pt', fontWeight: 700, color: c, lineHeight: 1,
          }}>
            {String(i + 1).padStart(2, '0')}
          </div>
          <div>
            <div style={{ fontSize: '11pt', fontWeight: 700, color: COLORS.navy, marginBottom: '1.5mm', lineHeight: 1.3 }}>
              {tr(q, lang)}
            </div>
            <div style={{ fontSize: '9pt', color: COLORS.slate, lineHeight: 1.5 }}>
              {tr(a, lang)}
            </div>
          </div>
        </div>
      ))}
    </div>

    <PageFooter />
  </Page>
);

const PageHandover = ({ lang, dir }) => (
  <Page dir={dir}>
    <PageHeader pageNum="09" lang={lang} />
    <div style={{ marginTop: '14mm' }}>
      <SectionLabel color={COLORS.navyLight}>{tr('p7_section', lang)}</SectionLabel>
      <PageTitle>{tr('p7_title', lang)}</PageTitle>
    </div>

    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '4mm', marginTop: '8mm' }}>
      {[
        ['p7_step1_title', 'p7_step1_body', COLORS.magenta, '01'],
        ['p7_step2_title', 'p7_step2_body', COLORS.gold, '02'],
        ['p7_step3_title', 'p7_step3_body', COLORS.navy, '03'],
      ].map(([title, body, c, n]) => (
        <div key={n} style={{
          padding: '6mm 5mm', background: '#fff',
          border: `1px solid ${COLORS.border}`,
          borderTop: `4px solid ${c}`, borderRadius: 4,
          minHeight: '110mm', display: 'flex', flexDirection: 'column',
        }}>
          <div style={{
            fontFamily: "'Playfair Display', Georgia, serif",
            fontSize: '46pt', fontWeight: 700, color: c, lineHeight: 1, letterSpacing: '-0.02em',
            marginBottom: '4mm',
          }}>
            {n}
          </div>
          <div style={{ fontSize: '12pt', fontWeight: 700, color: COLORS.navy, marginBottom: '3mm', lineHeight: 1.2 }}>
            {tr(title, lang)}
          </div>
          <div style={{ fontSize: '9.5pt', color: COLORS.slate, lineHeight: 1.55 }}>
            {tr(body, lang)}
          </div>
        </div>
      ))}
    </div>

    <Kicker color={COLORS.navyLight}>{tr('p7_kicker', lang)}</Kicker>
    <PageFooter />
  </Page>
);

const PageCheatSheet = ({ lang, dir }) => (
  <Page bg={COLORS.navy} dir={dir} last>
    <div style={{ position: 'absolute', top: '14mm', left: '14mm', right: '14mm' }}>
      <div style={{ fontSize: '8.5pt', letterSpacing: '0.2em', textTransform: 'uppercase', fontWeight: 700, color: COLORS.gold, marginBottom: '4mm' }}>
        {tr('p8_section', lang)}
      </div>
      <h2 style={{
        fontFamily: "'Playfair Display', Georgia, serif",
        fontSize: '36pt', fontWeight: 700, color: '#fff',
        lineHeight: 1.05, letterSpacing: '-0.02em', margin: 0,
      }}>
        {tr('p8_title', lang)}
      </h2>
    </div>

    <div style={{
      marginTop: '50mm',
      display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '5mm',
    }}>
      {[
        ['p8_we', 'p8_we_body', COLORS.magenta],
        ['p8_buyer', 'p8_buyer_body', COLORS.gold],
        ['p8_pitch', 'p8_pitch_body', COLORS.emerald],
        ['p8_hand', 'p8_hand_body', '#9BB0CC'],
      ].map(([title, body, c]) => (
        <div key={title} style={{
          padding: '6mm 6mm',
          background: 'rgba(255,255,255,0.04)',
          border: '1px solid rgba(255,255,255,0.12)',
          borderTop: `3px solid ${c}`, borderRadius: 4,
          minHeight: '50mm',
        }}>
          <div style={{ fontSize: '9pt', color: c, fontWeight: 800, letterSpacing: '0.16em', marginBottom: '3mm' }}>
            {tr(title, lang)}
          </div>
          <div style={{ fontSize: '11pt', color: '#E5EAF0', lineHeight: 1.5 }}>
            {tr(body, lang)}
          </div>
        </div>
      ))}
    </div>

    <div style={{
      position: 'absolute', bottom: '32mm', left: '14mm', right: '14mm',
      padding: '5mm 6mm', background: 'rgba(214, 51, 108, 0.18)',
      border: `1px solid ${COLORS.magenta}`, borderRadius: 4,
      fontSize: '12pt', color: '#fff', fontStyle: 'italic',
      lineHeight: 1.45, fontWeight: 500, textAlign: 'center',
    }}>
      {tr('p8_kicker', lang)}
    </div>

    <div style={{
      position: 'absolute', bottom: '14mm', left: '14mm', right: '14mm',
      paddingTop: '4mm', borderTop: '1px solid rgba(255,255,255,0.15)',
      display: 'flex', justifyContent: 'space-between',
      fontSize: '7.5pt', color: '#9BB0CC', fontWeight: 600,
    }}>
      <span style={{ letterSpacing: '0.08em', textTransform: 'uppercase' }}>Keystone by CloudVoro · Sales onboarding</span>
      <span style={{ letterSpacing: '0.08em', textTransform: 'uppercase' }}>10 / 10</span>
    </div>
  </Page>
);

// ─── Visual page (live screenshots with multilingual captions) ─────
const PageVisuals = ({
  lang, dir, pageNum, sectionKey, titleKey, kickerKey, kickerColor,
  shots, sectionColor = COLORS.magenta,
}) => (
  <Page dir={dir}>
    <PageHeader pageNum={pageNum} lang={lang} />
    <div style={{ marginTop: '14mm' }}>
      <SectionLabel color={sectionColor}>{tr(sectionKey, lang)}</SectionLabel>
      <PageTitle>{tr(titleKey, lang)}</PageTitle>
    </div>

    <div style={{
      display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '5mm',
      marginTop: '5mm',
    }}>
      {shots.map((s, i) => (
        <div key={i} style={{
          background: '#fff', border: `1px solid ${COLORS.border}`,
          borderTop: `3px solid ${s.color}`, borderRadius: 4,
          overflow: 'hidden', display: 'flex', flexDirection: 'column',
          minHeight: '85mm',
        }}>
          {/* Numbered tag */}
          <div style={{
            display: 'flex', alignItems: 'center', gap: '3mm',
            padding: '3mm 4mm 2mm 4mm', borderBottom: `1px solid ${COLORS.border}`,
          }}>
            <div style={{
              width: '7mm', height: '7mm', borderRadius: 4,
              background: s.color, color: '#fff',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontWeight: 800, fontSize: '10pt', letterSpacing: '0.02em',
            }}>
              {String(i + 1).padStart(2, '0')}
            </div>
            <div style={{
              fontSize: '9.5pt', fontWeight: 800, color: COLORS.navy,
              textTransform: 'uppercase', letterSpacing: '0.04em',
            }}>
              {tr(s.titleKey, lang)}
            </div>
          </div>
          {/* Screenshot */}
          <div style={{
            background: '#F4F5F8', padding: '2mm',
            flex: '0 0 auto',
          }}>
            <img
              src={s.src}
              alt=""
              style={{
                width: '100%', height: 'auto', display: 'block',
                borderRadius: 2, border: `1px solid ${COLORS.border}`,
              }}
            />
          </div>
          {/* Caption */}
          <div style={{
            padding: '3mm 4mm', fontSize: '8.5pt',
            color: COLORS.slate, lineHeight: 1.45, flex: 1,
          }}>
            {tr(s.captionKey, lang)}
          </div>
        </div>
      ))}
    </div>

    <Kicker color={kickerColor || sectionColor}>{tr(kickerKey, lang)}</Kicker>
    <PageFooter />
  </Page>
);

// ─── Toolbar (with language toggle) ────────────────────────────────
const FILENAME_BY_LANG = {
  en: 'Keystone-Sales-Primer-EN.pdf',
  ar: 'Keystone-Sales-Primer-AR.pdf',
  uk: 'Keystone-Sales-Primer-UK.pdf',
  ru: 'Keystone-Sales-Primer-RU.pdf',
};

const Toolbar = ({ lang, setLang }) => {
  const [downloading, setDownloading] = useState(false);

  const handleDownloadPDF = async () => {
    if (downloading) return;
    setDownloading(true);
    try {
      await downloadServerPDF(
        '/brochure/sales-primer',
        FILENAME_BY_LANG[lang] || FILENAME_BY_LANG.en,
        { lang },
      );
    } catch (err) {
      console.error('PDF export failed:', err);
      alert('PDF export failed. Please try the Print button as a fallback.');
    } finally {
      setDownloading(false);
    }
  };

  return (
  <div
    className="primer-toolbar no-print"
    style={{
      position: 'sticky', top: 0, zIndex: 100, background: '#fff',
      borderBottom: `1px solid ${COLORS.border}`, padding: '12px 24px',
      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      flexWrap: 'wrap', gap: 12, boxShadow: '0 2px 8px rgba(10, 37, 64, 0.06)',
    }}
  >
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
      <a
        href="/brochure/sales" data-testid="primer-back-to-brochure"
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          padding: '8px 14px', background: COLORS.bgSoft, color: COLORS.navy,
          border: `1px solid ${COLORS.border}`, borderRadius: 6,
          fontSize: 12.5, fontWeight: 600, textDecoration: 'none',
        }}
      >
        ← Back to brochure
      </a>
      <div style={{ borderLeft: `1px solid ${COLORS.border}`, paddingLeft: 12 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: COLORS.navy }}>Sales Primer</div>
        <div style={{ fontSize: 11, color: COLORS.muted }}>10 pages · A4 · 4 languages · Internal</div>
      </div>
    </div>
    <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
      {/* Lang switcher */}
      <div style={{ display: 'flex', gap: 4, background: COLORS.bgSoft, padding: 4, borderRadius: 6 }}>
        {LANGUAGES.map((l) => (
          <button
            key={l.code}
            data-testid={`lang-${l.code}`}
            onClick={() => setLang(l.code)}
            style={{
              padding: '6px 12px',
              background: lang === l.code ? COLORS.navy : 'transparent',
              color: lang === l.code ? '#fff' : COLORS.slate,
              border: 'none', borderRadius: 4,
              fontSize: 12.5, fontWeight: 700, cursor: 'pointer',
              letterSpacing: '0.02em',
              fontFamily: l.code === 'ar' ? "'Cairo', sans-serif" : 'inherit',
            }}
          >
            {l.label}
          </button>
        ))}
      </div>
      <button
        onClick={() => window.print()} data-testid="primer-print-btn"
        style={{
          background: '#fff', color: COLORS.navy,
          border: `1px solid ${COLORS.border}`,
          padding: '10px 18px', borderRadius: 6, fontSize: 12.5, fontWeight: 700,
          cursor: 'pointer', letterSpacing: '0.02em',
        }}
      >
        🖨  Print
      </button>
      <button
        onClick={handleDownloadPDF}
        disabled={downloading}
        data-testid="primer-download-pdf-btn"
        style={{
          background: downloading ? COLORS.muted : COLORS.magenta,
          color: '#fff', border: 'none',
          padding: '10px 22px', borderRadius: 6, fontSize: 13, fontWeight: 700,
          cursor: downloading ? 'wait' : 'pointer', letterSpacing: '0.02em',
          boxShadow: '0 4px 12px rgba(214, 51, 108, 0.3)',
          opacity: downloading ? 0.85 : 1,
        }}
      >
        {downloading ? 'Generating…' : '⬇  Download PDF'}
      </button>
    </div>
  </div>
  );
};

// ─── Content stack ─────────────────────────────────────────────────
const LANG_KEY = 'kx.sp.lang';
const VALID_LANGS = LANGUAGES.map((l) => l.code);

function readInitialLang() {
  try {
    const v = window.sessionStorage.getItem(LANG_KEY);
    if (v && VALID_LANGS.includes(v)) return v;
  } catch (_) { /* sessionStorage unavailable */ }
  return 'en';
}

function PrimerContent() {
  const [lang, setLangState] = useState(readInitialLang);
  const setLang = (next) => {
    setLangState(next);
    try { window.sessionStorage.setItem(LANG_KEY, next); } catch (_) { /* noop */ }
  };
  const dir = LANGUAGES.find((l) => l.code === lang)?.dir || 'ltr';
  return (
    <div style={{ background: '#E5E9F0', minHeight: '100vh' }} dir={dir}>
      <style>{`
        @page { size: A4; margin: 0; }
        @media print {
          html, body { background: #fff !important; margin: 0 !important; padding: 0 !important; }
          .no-print { display: none !important; }
          .primer-page {
            box-shadow: none !important; margin: 0 !important;
            page-break-after: always; break-after: page;
          }
          .primer-page:last-of-type { page-break-after: auto; break-after: auto; }
          .primer-stack { padding: 0 !important; }
        }
        body { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
      `}</style>

      <Toolbar lang={lang} setLang={setLang} />

      <div className="primer-stack" style={{ padding: '32px 16px 64px' }}>
        <PageCover lang={lang} dir={dir} />
        <PageWhatIs lang={lang} dir={dir} />
        <PageVisuals
          lang={lang} dir={dir} pageNum="03"
          sectionKey="vis1_section" titleKey="vis1_title"
          kickerKey="vis1_kicker" kickerColor={COLORS.magenta}
          sectionColor={COLORS.magenta}
          shots={[
            { titleKey: 'vis1_a_title', captionKey: 'vis1_a_caption', color: COLORS.magenta, src: '/sales-primer-shots/03-sales-order-entry.png' },
            { titleKey: 'vis1_b_title', captionKey: 'vis1_b_caption', color: COLORS.emerald, src: '/sales-primer-shots/04-batches-list.png' },
            { titleKey: 'vis1_c_title', captionKey: 'vis1_c_caption', color: COLORS.gold,    src: '/sales-primer-shots/08-inward-deliveries.png' },
            { titleKey: 'vis1_d_title', captionKey: 'vis1_d_caption', color: COLORS.navyLight, src: '/sales-primer-shots/10-supplier-codes.png' },
          ]}
        />
        <PageICP lang={lang} dir={dir} />
        <PageWhatItDoes lang={lang} dir={dir} />
        <PageVisuals
          lang={lang} dir={dir} pageNum="06"
          sectionKey="vis2_section" titleKey="vis2_title"
          kickerKey="vis2_kicker" kickerColor={COLORS.navyLight}
          sectionColor={COLORS.navyLight}
          shots={[
            { titleKey: 'vis2_a_title', captionKey: 'vis2_a_caption', color: COLORS.gold,      src: '/sales-primer-shots/02-sales-orders-pipeline.png' },
            { titleKey: 'vis2_b_title', captionKey: 'vis2_b_caption', color: COLORS.magenta,   src: '/sales-primer-shots/12-dispatch-note-open.jpg' },
            { titleKey: 'vis2_c_title', captionKey: 'vis2_c_caption', color: COLORS.magentaDeep, src: '/sales-primer-shots/14-recall-run.jpg' },
            { titleKey: 'vis2_d_title', captionKey: 'vis2_d_caption', color: COLORS.emerald,   src: '/sales-primer-shots/06-reports.png' },
          ]}
        />
        <PageHowToTalk lang={lang} dir={dir} />
        <PageQuestions lang={lang} dir={dir} />
        <PageHandover lang={lang} dir={dir} />
        <PageCheatSheet lang={lang} dir={dir} />
      </div>
    </div>
  );
}

export default function SalesPrimer() {
  return (
    <BrochureGate
      title="Sales primer · internal only."
      subtitle="This is the Keystone sales onboarding handbook. Internal use only, never share with prospects. Enter the access password to continue."
      buttonLabel="Unlock primer"
      eyebrow="Keystone by CloudVoro · Internal · Sales"
      tokenKey="kx.sp.tk"
    >
      <PrimerContent />
    </BrochureGate>
  );
}
